/**
 * The client's end of the event system: one place that turns "this
 * happened" into a toast and a sound.
 *
 * The server owns the event table and renders every message it sends, so
 * this file decides nothing about wording. What arrives is a `Notice` with
 * an event id on it; the id is looked up in the catalog the hello carried
 * (`Catalog.events`), and the row there says whether the news toasts, how
 * long it stays, which class it wears and which cue it plays.
 *
 * Three doors lead in:
 *
 * - `server(n)` — a notice off the wire.
 * - `local(id, params)` — the few events the server cannot see: the link
 *   going up or down, a chat line, a rejoin. Their rows carry a template
 *   and the client renders it with the same engine the server uses.
 * - `refusal(text)` — a refused order the client raised itself. The ~136
 *   server sites that refuse one name no event, so the hub tags the
 *   notice with the CLASS of the refusal instead (R596) and `server`
 *   handles it like any other tagged notice. A refusal with no class, and
 *   one the client raised before the wire, comes through this door and
 *   the generic `refusal` pseudo-event. An operator changes the toast and
 *   the buzz of one class at a time.
 *
 * Two rules keep an old client honest against a newer table:
 *
 * - An id this client has never heard of still shows its text. A message
 *   the server took the trouble to send is never eaten by a missing row.
 * - A row with no cue, or a catalog that has not arrived yet, is a silent
 *   no-op rather than an error — the same way a missing audio manifest
 *   leaves the director unavailable instead of breaking the game.
 */

import type { EventDefaults, EventSpec } from './gen/proto/aquaphobia';
import { EVENT_CLUB_PAID } from './gen/balance';
import { AUDIO_CLUB_PAYOUT_DELAY_SEC } from './gen/presentation';
import { render } from './tmpl';

/** The pseudo-event an unclassed error notice goes through. The two
 *  classed ones — `refusal_resources` and `refusal_requirement` (R596) —
 *  arrive tagged and need no constant here. The server ships all three
 *  rows in the catalog with the registered events. */
export const REFUSAL = 'refusal';

/**
 * How long the cue of one event waits, in milliseconds (R3134). The hub
 * pays the club share on the turn of the light, the frame of the day cue,
 * and the director speaks one line at a time, so the club line waits for
 * the day line to end. Every other cue plays at once.
 */
export function cueDelayMs(id: string): number {
  return id === EVENT_CLUB_PAID ? AUDIO_CLUB_PAYOUT_DELAY_SEC * 1000 : 0;
}

/** How the bus asks for a toast. The overlay owns the presentation; this
 *  is the whole vocabulary between them. */
export interface ToastRequest {
  error: boolean;
  ms?: number;
  cls?: string;
  key?: string;
  /** The place of the event, 0 or absent for none (R2370). The notice
   *  lane makes a line with a place a GO TO press. */
  cell?: number;
}

/** What the bus needs from the rest of the client. Both are structural, so
 *  a test hands it two functions and nothing else. */
export interface EventBusDeps {
  toast(text: string, o: ToastRequest): void;
  audio: { play(id: string): void };
  /** Runs a held cue later (`cueDelayMs`). A bus with none plays every
   *  cue at once. */
  later?(fn: () => void, ms: number): void;
}

/** One notice as it arrives, wire fields only. */
export interface ServerNotice {
  text: string;
  error: boolean;
  event: string;
  /** The cell of the emission, 0 for no place (R2370). */
  cell?: number;
}

export class EventBus {
  private specs = new Map<string, EventSpec>();
  private toastMs = 0;
  private dedupMs = 0;

  constructor(private deps: EventBusDeps) {}

  /** Takes the event table out of a hello catalog. A reconnect replaces
   *  it whole: the server is the authority and the rows can change under
   *  an operator's hand between two joins. */
  setCatalog(events: EventSpec[], defaults?: EventDefaults): void {
    this.specs.clear();
    for (const e of events) this.specs.set(e.id, e);
    this.toastMs = defaults?.toastMs ?? 0;
    this.dedupMs = defaults?.toastDedupMs ?? 0;
  }

  /** How long a toast stays by default, in milliseconds, or 0 before the
   *  hello, which leaves the overlay's own default in place. */
  get defaultToastMs(): number { return this.toastMs; }
  /** How close together two identical toasts must be for the second to
   *  restart the first. The overlay is told this once, on the hello. */
  get defaultDedupMs(): number { return this.dedupMs; }

  /** A notice off the wire. */
  server(n: ServerNotice): void {
    // Untagged: a refused order. It is the one case the client decides,
    // because the refusing site never named an event.
    if (!n.event) {
      if (n.error) { this.refusal(n.text); return; }
      if (n.text) this.deps.toast(n.text, { error: false });
      return;
    }
    const spec = this.specs.get(n.event);
    if (!spec) {
      // A newer table than this build knows. Show the words anyway.
      if (n.text) this.deps.toast(n.text, { error: n.error, key: n.event, cell: n.cell || undefined });
      return;
    }
    this.fire(spec, n.text, n.error, n.cell);
  }

  /** An event the client raises for itself. Its row carries the template,
   *  because there is no server rendering to do it. */
  local(id: string, params?: Record<string, string>): void {
    const spec = this.specs.get(id);
    if (!spec) return; // no catalog yet, or a table without this row
    const text = spec.toast ? render(spec.toastTemplate, params ?? {}) : '';
    this.fire(spec, text, false);
  }

  /** A refused order. */
  refusal(text: string): void {
    const spec = this.specs.get(REFUSAL);
    // Before the first hello there is no table. A refusal still has to be
    // seen — the landing screen refuses joins — so it toasts plainly.
    if (!spec) {
      if (text) this.deps.toast(text, { error: true, key: REFUSAL });
      return;
    }
    this.fire(spec, text, true);
  }

  /** The single fan-out: one row, the text it goes with, both sinks.
   *
   *  Empty text is a cue-only event: the sound plays and nothing is shown.
   *  That is how a siege opening and the seaquake stages arrive. */
  private fire(spec: EventSpec, text: string, error: boolean, cell?: number): void {
    if (spec.toast && text) {
      this.deps.toast(text, {
        // Two error flags, combined: the row marks an event that is always
        // bad news, and the emission marks the half of an event that is
        // bad news only for this side.
        error: error || spec.toastError,
        ms: spec.toastMs || this.toastMs || undefined,
        key: spec.id,
        // The place rides the toast to the notice lane (R2370).
        cell: cell || undefined,
      });
    }
    if (!spec.sfx) return;
    const sfx = spec.sfx;
    const wait = cueDelayMs(spec.id);
    if (wait > 0 && this.deps.later) this.deps.later(() => this.deps.audio.play(sfx), wait);
    else this.deps.audio.play(sfx);
  }
}
